import dateFormat from 'date-fns/format';
import { getCurrentDateFormatted } from 'utils/common';

const convertToAmPm = (hour) => {
  if (hour < 0 || hour > 23) return null;
  const specailCase = (hour > 12 ? hour - 12 : hour);
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === '0' ? 12 : specailCase;
  return `${hour12} ${period}`;
};

export const getNestedValue = (obj, keys, defaultValue = null) => keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj) ?? defaultValue;

export const getAqiConvertedData = (unformattedData) => {
  const formattedData = unformattedData && Array.isArray(unformattedData) && unformattedData.length > 0 && unformattedData.map((obj) => ({
    timestamp: obj?.last_updated || obj.date,
    avg: obj.componentMap && Object.keys(obj.componentMap).length > 0 && obj.componentMap['AQI-IN']?.sensorData && getNestedValue(obj, ['componentMap', 'AQI-IN', 'sensorData']),
    id: obj?.cityCode,
    name: obj?.cityName,
    state_name: obj?.stateName,
    district_id: obj?.cityCode,
    hour: obj?.hour && convertToAmPm(obj.hour),
    day: obj?.date && formatDayOfWeek(obj.date),
  }));
  return formattedData;
};

export const getWeatherMeterWidget = hallOfFameData => {
  let currentYearData =
    hallOfFameData && Array.isArray(hallOfFameData) ? getAqiConvertedData(hallOfFameData) : (
    Array.isArray(hallOfFameData.sectionitems) &&
    hallOfFameData.sectionitems?.length > 0 &&
    Array.isArray(hallOfFameData.sectionitems[0]?.weeklyCurentYearAQIData) &&
    hallOfFameData.sectionitems[0]?.weeklyCurentYearAQIData?.length > 0 &&
    getAqiConvertedData(hallOfFameData.sectionitems[0]?.weeklyCurentYearAQIData));

  const currentYearSeriesDataFormat =
    currentYearData &&
    Array.isArray(currentYearData) &&
    currentYearData?.length > 0 &&
    currentYearData?.map(item => item?.avg);

  const isHourlyData = currentYearData &&
  Array.isArray(currentYearData) &&
  currentYearData?.length > 0 && currentYearData.every((item)=> item.hour);

  const isWeeklyData = currentYearData &&
  Array.isArray(currentYearData) &&
  currentYearData?.length > 0 && currentYearData.length === 7

  let xSeries = isHourlyData ? currentYearData &&
  Array.isArray(currentYearData) &&
  currentYearData?.length > 0 && currentYearData.map((item)=>item?.hour) : currentYearData &&
  Array.isArray(currentYearData) &&
  currentYearData?.length > 0 && currentYearData.map((item)=> getCurrentDateFormatted(item?.timestamp,'day-month'))

  xSeries = isWeeklyData ? currentYearData &&
  Array.isArray(currentYearData) &&
  currentYearData?.length > 0 && currentYearData.map((item)=> getCurrentDateFormatted(item?.timestamp,'day-month')) : xSeries;

  let xAxisInterval = isHourlyData ? 2 : 5
  xAxisInterval = isWeeklyData ? 1 : xAxisInterval

  const endValue = currentYearSeriesDataFormat && Array.isArray(currentYearSeriesDataFormat) && currentYearSeriesDataFormat.length > 0 && Math.max(...currentYearSeriesDataFormat);
  const startValue = currentYearSeriesDataFormat && Array.isArray(currentYearSeriesDataFormat) && currentYearSeriesDataFormat.length > 0 && Math.min(...currentYearSeriesDataFormat);
  const weatherMeterWidget = [
    {
      seriesdata: currentYearSeriesDataFormat,
      sector: 'Last year',
      fundstext: 'AQI',
      hovertext: 'Last Year',
      growth: '100.4',
      yAxisRange: {
        start: startValue < 50 ? 0 : startValue - 50,
        end: endValue + 50,
        interval: '50',
      },
      xAxisRange:xSeries && Array.isArray(xSeries) && xSeries.length > 0 && xSeries || [],
      xAxisInterval

    },
  ];

  return weatherMeterWidget;
};

export const getWeeklyColumns = () => {
  const columns = [
    { header: 'Days', accessor: 'timestamp', className: 'w_25' },
    { header: 'Index', accessor: 'index', className: 'w_25' },
    { header: 'AQI Status', accessor: 'status', className: 'w_25' },
  ];
  return columns;
};
export const getColumns = () => {
  const columns = [
    { header: 'Rank', accessor: 'rank', className: 'rank' },
    {
      header: 'City',
      accessor: 'name',
      accessorState: 'state_name',
      className: 'city',
    },
    { header: 'Aqi', accessor: 'avg', className: 'aqi' },
  ];

  return columns;
};

const formatDayOfWeek = (dateStr) => {
  const [day, month, year] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date);
};

export const pollutantMapping = {
  pm25: 'Particular Matter',
  pm10: 'Particular Matter',
  co: 'Carbon Monoxide',
  so2: 'Sulphar Dioxide',
  no2: 'Nitrogen Dioxide',
  o3: 'Ozone',
};

export const getAQIClassName = (pollutant, level) => {
  switch (pollutant) {
    case 'pm25':
      switch (true) {
        case level <= 30:
          return 'good_lvl';
        case level <= 60:
          return 'moderate_lvl';
        case level <= 90:
          return 'poor_lvl';
        case level <= 120:
          return 'unhealthy_lvl';
        case level <= 250:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    case 'pm10':
      switch (true) {
        case level <= 50:
          return 'good_lvl';
        case level <= 100:
          return 'moderate_lvl';
        case level <= 250:
          return 'poor_lvl';
        case level <= 350:
          return 'unhealthy_lvl';
        case level <= 430:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    case 'co':
      switch (true) {
        case level <= 8330:
          return 'good_lvl';
        case level <= 16670:
          return 'moderate_lvl';
        case level <= 25000:
          return 'poor_lvl';
        case level <= 33330:
          return 'unhealthy_lvl';
        case level <= 41670:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    case 'so2':
      switch (true) {
        case level <= 40:
          return 'good_lvl';
        case level <= 80:
          return 'moderate_lvl';
        case level <= 380:
          return 'poor_lvl';
        case level <= 800:
          return 'unhealthy_lvl';
        case level <= 1600:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    case 'no2':
      switch (true) {
        case level <= 40:
          return 'good_lvl';
        case level <= 80:
          return 'moderate_lvl';
        case level <= 180:
          return 'poor_lvl';
        case level <= 190:
          return 'unhealthy_lvl';
        case level <= 400:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    case 'o3':
      switch (true) {
        case level <= 50:
          return 'good_lvl';
        case level <= 100:
          return 'moderate_lvl';
        case level <= 168:
          return 'poor_lvl';
        case level <= 208:
          return 'unhealthy_lvl';
        case level <= 748:
          return 'severe_lvl';
        default:
          return 'hazardous_lvl';
      }

    default:
      return '';
  }
};

export const getSpecificDataForPassedKeys = (input, keys) =>
  keys
    .map((key) => {
      if (input && key && input[key]) {
        return { ...input[key], name: pollutantMapping[key] };
      }
      return null;
    })
    .filter((item) => item);

    export const getAQIRangeText = (todayAqiIndex) => {
      if (Number.isNaN(todayAqiIndex)) {
        return '';
      }
    
      switch (!!todayAqiIndex) {
        case todayAqiIndex > 0 && todayAqiIndex <= 50:
          return 'Good';
        case todayAqiIndex > 50 && todayAqiIndex <= 100:
          return 'Moderate';
        case todayAqiIndex > 100 && todayAqiIndex <= 200:
          return 'Poor';
        case todayAqiIndex > 200 && todayAqiIndex <= 300:
          return 'Unhealthy';
        case todayAqiIndex > 300 && todayAqiIndex <= 400:
          return 'Severe';
        case todayAqiIndex > 400:
          return 'Hazardous';
        default:
          return '';
      }
    };
